import { useTheme } from '@mui/material/styles';
import { FONTS } from '../../../../config/app-config';
import React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const ROLE_OPTIONS = ['Docs-Admin'];

export default function ApiKeyDialog({
    open,
    mode,
    formData,
    generatedKey,
    saving,
    onChange,
    onClose,
    onSave,
    onCopy,
}) {
    const theme = useTheme();
    const isEditing = mode === 'edit';
    const hasGeneratedKey = Boolean(generatedKey);
    const ttl = Number(formData.ttl);
    const canSave = (
        Number.isInteger(ttl)
        && ttl >= 1
        && ttl <= 365
        && formData.roles.length > 0
        && !saving
    );

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle
                sx={{ m: 0, p: 2, bgcolor: theme.palette.primary.main, color: 'common.white' }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography
                        variant="h6"
                        sx={{ fontFamily: FONTS.heading, fontWeight: 700 }}
                    >
                        {hasGeneratedKey
                            ? 'API Key Created'
                            : isEditing ? 'Update API Key' : 'Create API Key'}
                    </Typography>
                    <IconButton edge="end" color="inherit" onClick={onClose} disabled={saving}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: '24px !important' }}>
                {hasGeneratedKey ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Alert severity="warning">
                            Copy this API key now. It cannot be shown again after this dialog is closed.
                        </Alert>
                        <TextField
                            label="API Key"
                            value={generatedKey}
                            type="password"
                            fullWidth
                            autoFocus
                            onFocus={(event) => event.target.select()}
                            inputProps={{ readOnly: true, 'aria-label': 'Generated API key' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Copy API Key">
                                            <IconButton
                                                edge="end"
                                                onClick={onCopy}
                                                aria-label="Copy API key"
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                            helperText="Select the text to copy it manually, or use the copy button."
                        />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={(event) => onChange({
                                ...formData,
                                description: event.target.value,
                            })}
                            placeholder="What will this key be used for?"
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label="Lifetime (days)"
                            name="ttl"
                            value={formData.ttl}
                            onChange={(event) => onChange({
                                ...formData,
                                ttl: event.target.value,
                            })}
                            type="number"
                            fullWidth
                            inputProps={{ min: 1, max: 365, step: 1 }}
                            error={formData.ttl !== '' && (!Number.isInteger(ttl) || ttl < 1 || ttl > 365)}
                            helperText="API keys can be valid for 1 to 365 days. Updating resets the expiry from today."
                        />
                        <Autocomplete
                            multiple
                            freeSolo
                            options={ROLE_OPTIONS}
                            value={formData.roles}
                            onChange={(_event, value) => onChange({
                                ...formData,
                                roles: value
                                    .map((role) => String(role).trim())
                                    .filter(Boolean),
                            })}
                            renderTags={(value, getTagProps) => value.map((role, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={role}
                                    label={role}
                                />
                            ))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Roles"
                                    error={formData.roles.length === 0}
                                    helperText="Press Enter to add a role. Docs-Admin is required for protected admin endpoints."
                                />
                            )}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                {hasGeneratedKey ? (
                    <Button
                        variant="contained"
                        onClick={onClose}
                        sx={{ backgroundColor: theme.palette.primary.main, color: '#fff' }}
                    >
                        Done
                    </Button>
                ) : (
                    <>
                        <Button onClick={onClose} color="error" disabled={saving}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={onSave}
                            disabled={!canSave}
                            sx={{ backgroundColor: theme.palette.primary.main, color: '#fff', minWidth: 92 }}
                        >
                            {saving
                                ? <CircularProgress size={20} color="inherit" />
                                : isEditing ? 'Update' : 'Create'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
